import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import AuthService from "../../common/AuthService";
import {
  getLicences,
  IGetLicencesResponceModel,
} from "../../webapi/service/getLicences";
import { registCode } from "../../webapi/service/registCode";
import { registSerial } from "../../webapi/service/registSerial";
import { registTerm } from "../../webapi/service/registTerm";
import PromptDialog, { InputTextItem } from "../../common/PromptDialog";
import { IConfirmDialogParams } from "../../common/ConfirmDialog";
import SerialList, { MaxMemoLength } from "./SerialList";
import "../main.css";
import "./license.css";
import { updateSerial } from "../../webapi/service/updateSerial";

function createList(title: string, array: string[]) {
  const tr: JSX.Element[] = [];
  array.forEach((str, i) => {
    tr.push(
      <tr key={i}>
        <td>{str}</td>
      </tr>
    );
  });
  return (
    <table>
      <thead>
        <tr>
          <th>{title}</th>
        </tr>
      </thead>
      <tbody>{tr}</tbody>
    </table>
  );
}

export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (params: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 非操作タイムアウトをリセットします
   */
  resetUnoperatedTimeout: () => void;
}

interface IState extends IGetLicencesResponceModel {
  /**
   * シリアル登録ダイアログを表示する場合true
   */
  showSerial: boolean;
  /**
   * ライセンス登録ダイアログを表示する場合はtrue
   */
  showLicense: boolean;
  /**
   * ライセンス登録ダイアログ表示モード
   * REGIST_MODEL_LICENSE | REGIST_TERM_LICENSE
   */
  registLicense: number;
  /**
   * シリアル登録ダイアログシリアル番号初期表示
   */
  serialText: string;
  /**
   * シリアル登録ダイアログメモ初期表示
   */
  serialMemo: string;
}

class LicenseMainSpace extends React.Component<IProps, IState> {
  private readonly REGIST_MODEL_LICENSE = 0;
  private readonly REGIST_TERM_LICENSE = 1;

  constructor(props: IProps) {
    super(props);

    this.state = {
      max: 0,
      registerd: 0,
      codes: [],
      serials: [],
      termcode: "",
      termexpiration: "",
      showSerial: false,
      showLicense: false,
      registLicense: this.REGIST_MODEL_LICENSE,
      serialText: "",
      serialMemo: "",
    };

    this.handleShowSerialDialog = this.handleShowSerialDialog.bind(this);
    this.handleHideSerialDialog = this.handleHideSerialDialog.bind(this);
    this.handleRegistSerial = this.handleRegistSerial.bind(this);
    this.handleUpdateSerial = this.handleUpdateSerial.bind(this);
    this.handleShowLicenseDialog = this.handleShowLicenseDialog.bind(this);
    this.handleRegistLicense = this.handleRegistLicense.bind(this);
    this.handleRegistTermLicense = this.handleRegistTermLicense.bind(this);
  }

  componentDidMount() {
    this.loadLicense(true);
  }

  loadLicense(loading: boolean) {
    const { authService, onLoading, onCommunicationError } = this.props;
    onLoading(loading);
    getLicences({
      authService: authService,
      success: (max, registerd, codes, serials, termcode, termexpiration) => {
        this.setState({
          max: max,
          registerd: registerd,
          codes: codes,
          serials: serials,
          termcode: termcode,
          termexpiration: termexpiration,
        });
        onLoading(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleShowSerialDialog() {
    this.props.resetUnoperatedTimeout();
    this.setState({ showSerial: true, serialText: "", serialMemo: "" });
  }

  handleHideSerialDialog() {
    this.setState({ showSerial: false, serialText: "", serialMemo: "" });
  }

  handleRegistSerial(texts: InputTextItem[]) {
    const serial = texts[0].text;
    const memo = texts[1].text;
    const { showConfirm, t } = this.props;
    this.setState({ showSerial: false, serialText: serial, serialMemo: memo });
    showConfirm({
      title: t("license_serial_register_confirm"),
      message: t("license_serial_register_confirm_message") + "\n" + serial,
      onOk: () => {
        const {
          authService,
          onLoading,
          onCommunicationError,
          resetUnoperatedTimeout,
        } = this.props;
        onLoading(true);
        resetUnoperatedTimeout();
        registSerial({
          authService,
          serial: serial,
          memo: memo,
          success: () => {
            this.loadLicense(false);
          },
          fail: (id, status) => {
            onCommunicationError(id, status);
          },
        });
      },
      onCancel: () => {
        this.setState({ showSerial: true });
      },
    });
  }

  handleUpdateSerial(serial: string, memo: string) {
    const {
      authService,
      onLoading,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    onLoading(true);
    resetUnoperatedTimeout();
    updateSerial({
      authService: authService,
      serial: serial,
      memo: memo,
      success: () => {
        this.loadLicense(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleShowLicenseDialog(show: boolean, registLicense?: number) {
    if (this.state.showLicense !== show) {
      this.props.resetUnoperatedTimeout();
      if (typeof registLicense !== "undefined") {
        this.setState({ showLicense: show, registLicense: registLicense });
      } else {
        this.setState({ showLicense: show });
      }
    }
  }

  handleRegistLicense(texts: InputTextItem[]) {
    const { authService, onLoading, onCommunicationError } = this.props;
    const { text } = texts[0];
    onLoading(true);
    this.handleShowLicenseDialog(false);
    registCode({
      authService,
      code: text,
      success: () => {
        this.loadLicense(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  handleRegistTermLicense(texts: InputTextItem[]) {
    const { authService, onLoading, onCommunicationError } = this.props;
    const { text } = texts[0];
    onLoading(true);
    this.handleShowLicenseDialog(false);
    registTerm({
      authService,
      termcode: text,
      success: () => {
        this.loadLicense(false);
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  render() {
    const { t } = this.props;
    const {
      max,
      registerd,
      codes,
      serials,
      termcode,
      termexpiration,
      showSerial,
      showLicense,
      registLicense,
      serialText,
      serialMemo,
    } = this.state;
    const serialBtn =
      codes.length > 0 ? (
        <Button className="btn-mainspace" onClick={this.handleShowSerialDialog}>
          {t("license_serial_register")}
        </Button>
      ) : (
        ""
      );
    const codeList = createList(t("license_license_list"), codes);
    const termexpirationList = createList(t("license_termexpiration"), [
      termexpiration,
    ]);
    const termcodeList = createList(t("license_termcode"), [termcode]);
    const ok =
      registLicense === this.REGIST_MODEL_LICENSE
        ? this.handleRegistLicense
        : this.handleRegistTermLicense;
    const downloadLicense = (
      <React.Fragment>
        <div className="mainspace-title">
          <h3>{t("license_download")}</h3>
        </div>
        <div className="mainspace-toolbar">
          {serialBtn}
          <Button
            className="btn-mainspace"
            onClick={() =>
              this.handleShowLicenseDialog(true, this.REGIST_MODEL_LICENSE)
            }
          >
            {t("license_license_register")}
          </Button>
        </div>
        <div className="license-count">
          <div className="item">
            <div className="title">{t("license_serial_all")} :</div>
            <div>{max}</div>
          </div>
          <div className="item">
            <div className="title">{t("license_serial_left")} :</div>
            <div>{max - registerd}</div>
          </div>
        </div>
        <div className="license-list">
          <div className="serial-item">
            <SerialList
              serials={serials}
              onUpdateSerial={this.handleUpdateSerial}
            />
          </div>
          <div className="code-item">{codeList}</div>
        </div>
      </React.Fragment>
    );
    const cloudLicense = (
      <React.Fragment>
        <div className="mainspace-title">
          <h3>{t("license_cloud")}</h3>
        </div>
        <div className="mainspace-toolbar">
          <Button
            className="btn-mainspace"
            onClick={() =>
              this.handleShowLicenseDialog(true, this.REGIST_TERM_LICENSE)
            }
          >
            {t("license_license_register")}
          </Button>
        </div>
        <div className="cloud-license-list">
          <div className="item">{termexpirationList}</div>
          <div className="item">{termcodeList}</div>
        </div>
      </React.Fragment>
    );
    const contents = (
      <div style={{ overflow: "auto" }}>
        <div className="mainspace">
          {downloadLicense}
          {cloudLicense}
        </div>
        <PromptDialog
          show={showSerial}
          title={t("license_serial_register")}
          items={[
            {
              id: "serial",
              label: t("license_serial_register_serialnum"),
              placeholder: t("err_license_serial_register"),
              text: serialText,
              validate: { length: { min: 9, max: 9 } },
              inputValue: (item, value) => {
                const regex = /^S|^S[0-9a-zA-Z]+$/;
                if (value.length === 0) {
                  item.text = value;
                } else if (value.length <= 9 && regex.test(value)) {
                  item.text = value.toUpperCase();
                }
              },
            },
            {
              id: "memo",
              label: t("license_serial_register_memo"),
              text: serialMemo,
              maxLength: MaxMemoLength,
            },
          ]}
          onOk={this.handleRegistSerial}
          onCancel={this.handleHideSerialDialog}
        />
        <PromptDialog
          show={showLicense}
          title={t("license_license_register")}
          items={[
            {
              id: "license",
              label: t("license_license_register_serialnum"),
              placeholder: t("err_license_license_register"),
              validate: { length: { min: 19, max: 19 } },
              inputValue: (item, value) => {
                const regex = /^[0-9a-zA-Z]+$/;
                const replace = value.replace(/-/g, "");
                if (value.length === 0) {
                  item.text = value;
                } else if (replace.length <= 16 && regex.test(replace)) {
                  let t = replace;
                  const idx1 = 3;
                  const idx2 = 8;
                  const idx3 = 14;
                  const compleate = "-";
                  if (t.length > idx1 && t[idx1] !== compleate) {
                    t = t.substring(0, idx1) + compleate + t.substring(idx1);
                  }
                  if (t.length > idx2 && t[idx2] !== compleate) {
                    t = t.substring(0, idx2) + compleate + t.substring(idx2);
                  }
                  if (t.length > idx3 && t[idx3] !== compleate) {
                    t = t.substring(0, idx3) + compleate + t.substring(idx3);
                  }
                  item.text = t.toUpperCase();
                }
              },
            },
          ]}
          onOk={ok}
          onCancel={() => this.handleShowLicenseDialog(false)}
        />
      </div>
    );
    return contents;
  }
}

export default withTranslation()(LicenseMainSpace);
